import {Timeline} from "@zendeskgarden/react-accordions";
import {Field, Input, Label} from "@zendeskgarden/react-forms";
import {DrawerModal} from "@zendeskgarden/react-modals";
import {Pagination} from "@zendeskgarden/react-pagination";
import {ReactComponent as UserIcon} from "@zendeskgarden/svg-icons/src/12/user-solo-stroke.svg";
import {ReactElement, useContext, useEffect, useState} from "react";
import {useAdminSearchAuditLogLazyQuery} from "../../../../__generated__/generated_types";
import {AuthContext} from "../../../../contexts/AuthContext";
import {useChartOfAccounts} from "../../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {useProperties} from "../../../../contexts/properties/PropertiesContext";
import {RenderAuditLogVariables} from "../../../admin/audit-log/RenderAuditLogVariables";
import {mapApiName} from "../../../admin/audit-log/api-mappings";
import {renderAuditLogUsername} from "../../../admin/audit-log/common";
import * as css from "./styles/AccountAuditLogSidebar.module.scss";

export interface AccountAuditLogSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    accountId: string;
    propertyId: string;
}

export function AccountAuditLogSidebar(props: AccountAuditLogSidebarProps): ReactElement {
    const {properties} = useProperties();
    const {accounts} = useChartOfAccounts();
    const {user, operationalMetricTypes} = useContext(AuthContext);

    const [searchAuditLog, {data, loading}] = useAdminSearchAuditLogLazyQuery({
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: true
    });

    const [page, setPage] = useState<number>(1);
    const [beforeTime, setBeforeTime] = useState<string | null>(null);
    const [afterTime, setAfterTime] = useState<string | null>(null);

    const dateFormat = new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "numeric"
    });

    useEffect(() => {
        searchAuditLog({
            variables: {
                page: page,
                accountId: props.accountId,
                propertyId: props.propertyId,
                beforeTime: beforeTime ? new Date(beforeTime).toISOString() : undefined,
                afterTime: afterTime ? new Date(afterTime).toISOString() : undefined,
            }
        });
    }, [page, beforeTime, afterTime]);

    return <DrawerModal isOpen={props.isOpen} onClose={props.onClose}>
        <DrawerModal.Header>Audit Log</DrawerModal.Header>
        <DrawerModal.Body className={css.accountAuditLogSidebar}>
            <div className={css.form}>
                <form>
                    <Field className={css.formField}>
                        <Label>Before</Label>
                        <Input
                                type="datetime-local"
                                value={beforeTime ?? ""}
                                onChange={(e) => {
                                    if(e.target.value) {
                                        setBeforeTime(e.target.value);
                                    } else {
                                        setBeforeTime(null);
                                    }
                                }}
                        />
                    </Field>
                    <Field className={css.formField}>
                        <Label>After</Label>
                        <Input
                                type="datetime-local"
                                value={afterTime ?? ""}
                                onChange={(e) => {
                                    if(e.target.value) {
                                        setAfterTime(e.target.value);
                                    } else {
                                        setAfterTime(null);
                                    }
                                }}
                        />
                    </Field>
                </form>
            </div>
            <div>
                {(data?.searchAuditTrail && data.searchAuditTrail.items.length > 0) ?
                    <Timeline>
                        {data.searchAuditTrail.items.map((item, idx) => {
                            return <Timeline.Item key={idx} icon={<UserIcon style={{backgroundColor: "var(--magenta-500)", color: "var(--white)", padding: "2px", borderRadius: "50%"}} />}>
                                <Timeline.Content>
                                <div className={css.userTimeHeader}>
                                    <div className={css.userAndOperation}>{renderAuditLogUsername(item.user)} updated {mapApiName(item.operationName)}</div>
                                    <div className={css.time}>{dateFormat.format(new Date(item.performedAt))}</div>
                                </div>
                                <div>
                                {user && item.variables &&
                                    <RenderAuditLogVariables
                                        user={user}
                                        variables={item.variables}
                                        accounts={accounts}
                                        properties={properties ?? []}
                                        operationalMetricTypes={operationalMetricTypes}
                                        operationName={item.operationName}
                                        enrichment={item.enrichment ?? undefined}
                                    />
                                }
                                </div>
                                </Timeline.Content>
                            </Timeline.Item>;
                        })}
                    </Timeline> :
                    <span>{loading ? "Loading Logs..." : "No Logs Available"}</span>
                }
            </div>
        </DrawerModal.Body>
        <DrawerModal.Footer className={css.pagination}>
            <nav aria-label="Results Page Navigation">
                <Pagination currentPage={page} totalPages={data?.searchAuditTrail.maxPage ?? 1} onChange={newPage => setPage(newPage)} />
            </nav>
        </DrawerModal.Footer>
        <DrawerModal.Close />
    </DrawerModal>;
}